import React, { useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { MediaAssetInput } from '../settings/MediaAssetPicker';
import { mediaUrl } from '../media/MediaAssetLibrary';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import WgiymEditor from '../ui/WgiymEditor';
import { CrmButton, CrmCard, CrmInput, CrmSelect, CrmSwitch, CrmTextarea } from '../ui';
import { WEEKDAYS, formatDateTimeForPreference, parsePreferredDateTime } from '../settings/settingsFormUtils';
import { getGlobalDateFormat } from '../../utils/dateFormatter';
import { useStorefrontCms } from '../../services/fusionBoxCmsService';
import './popup-manager.scss';

const emptyPopup = {
  name: '', popup_type: 'offer', title: '', subtitle: '', body_html: '', image_url: '', cta_text: '', cta_link: '',
  form_enabled: false, form_fields_json: '[{"name":"name","label":"Name"},{"name":"whatsapp_number","label":"WhatsApp number"}]',
  excluded_paths: '/checkout,/success,/thank-you', days_of_week: '', cooldown_days: 7, availability_enabled: true, max_once_per_session: true,
  is_active: false, delay_seconds: 3, frequency: 'once_per_session', target_scope: 'all',
  target_page_slugs: '', trigger_type: 'after_seconds', scroll_percent: 50,
  starts_at: '', ends_at: '', display_order: 0,
};

const frequencies = [
  { value: 'every_visit', label: 'Every visit' }, { value: 'once_per_session', label: 'Once per session' },
  { value: 'once_per_day', label: 'Once per day' }, { value: 'once_ever', label: 'Once ever per browser' },
  { value: 'cooldown_days', label: 'Cooldown days' },
];
const popupTypes = [
  { value: 'offer', label: 'Offer' }, { value: 'exit_intent', label: 'Exit Intent / Lead Capture' },
  { value: 'flash', label: 'Flash / Weekend' }, { value: 'lead_capture', label: 'Lead Capture' }, { value: 'notice', label: 'Notice' },
];
const scopes = [
  { value: 'all', label: 'All website' }, { value: 'homepage', label: 'Homepage only' },
  { value: 'selected_pages', label: 'Selected CMS pages' }, { value: 'product_pages', label: 'Product pages' },
  { value: 'shop_pages', label: 'Shop + Build Box pages' }, { value: 'checkout_pages', label: 'Checkout/cart pages' },
];
const triggers = [
  { value: 'page_load', label: 'Immediately on page load' }, { value: 'after_seconds', label: 'After delay seconds' },
  { value: 'scroll_percent', label: 'After scroll percentage' }, { value: 'exit_intent', label: 'Exit intent, desktop' },
];
const splitCsv = (value) => String(value || '').split(',').map((part) => part.trim()).filter(Boolean);
const weekdayNumberForIndex = (index) => (index + 1) % 7;
const selectedWeekdayNumbers = (value) => new Set(splitCsv(value).map(Number).filter(Number.isInteger));
const optionList = (items) => items.map((option) => <option key={option.value} value={option.value}>{option.label}</option>);

export default function PopupManager() {
  const { fetchPopups, fetchCmsPages, createPopup, updatePopupById, deletePopupById } = useStorefrontCms();
  const mediaRef = useRef(null);
  const dateFormat = getGlobalDateFormat();
  const [popups, setPopups] = useState([]);
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyPopup);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });
  const patch = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const selectPopup = (popup) => {
    setSelected(popup);
    setForm({
      name: popup.name || popup.title || '', title: popup.title || '', body_html: popup.body_html || '', image_url: popup.image_url || '',
      cta_text: popup.cta_text || '', cta_link: popup.cta_link || '', is_active: Boolean(popup.is_active), delay_seconds: popup.delay_seconds ?? 3,
      popup_type: popup.popup_type || 'offer', subtitle: popup.subtitle || '', form_enabled: Boolean(popup.form_enabled),
      form_fields_json: popup.form_fields_json || emptyPopup.form_fields_json, excluded_paths: popup.excluded_paths || emptyPopup.excluded_paths,
      days_of_week: popup.days_of_week || '', cooldown_days: popup.cooldown_days ?? 7,
      availability_enabled: popup.availability_enabled !== undefined ? Boolean(popup.availability_enabled) : true,
      max_once_per_session: popup.max_once_per_session !== undefined ? Boolean(popup.max_once_per_session) : true,
      frequency: popup.frequency || 'cooldown_days', target_scope: popup.target_scope || 'all', target_page_slugs: popup.target_page_slugs || '',
      trigger_type: popup.trigger_type || 'after_seconds', scroll_percent: popup.scroll_percent ?? 50,
      starts_at: formatDateTimeForPreference(popup.starts_at, dateFormat), ends_at: formatDateTimeForPreference(popup.ends_at, dateFormat),
      display_order: popup.display_order ?? 0,
    });
    setImagePreview(popup.image_url ? mediaUrl(popup.image_url) : '');
  };

  const load = async () => {
    setLoading(true);
    try {
      const [popupRows, pageRows] = await Promise.all([fetchPopups(), fetchCmsPages()]);
      const nextPopups = Array.isArray(popupRows) ? popupRows : [];
      setPopups(nextPopups);
      setPages(Array.isArray(pageRows) ? pageRows : pageRows?.pages || []);
      if (!selected && nextPopups[0]) selectPopup(nextPopups[0]);
    } catch (error) { notify(error.response?.data?.error || 'Failed to load popups.', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const newPopup = () => { setSelected(null); setForm(emptyPopup); setImagePreview(''); };
  const toFormData = () => {
    const fd = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      let nextValue = value;
      if (key === 'starts_at' || key === 'ends_at') nextValue = value ? parsePreferredDateTime(value, dateFormat) : '';
      if (['is_active', 'form_enabled', 'availability_enabled', 'max_once_per_session'].includes(key)) fd.append(key, nextValue ? 1 : 0);
      else if (nextValue !== undefined && nextValue !== null) fd.append(key, nextValue);
    });
    fd.set('image_url', form.image_url || '');
    return fd;
  };

  const save = async () => {
    if (!form.name.trim() && !form.title.trim()) return notify('Popup name or title is required', 'error');
    if (form.form_enabled) {
      try { if (!Array.isArray(JSON.parse(form.form_fields_json || '[]'))) return notify('Form fields must be a JSON array', 'error'); }
      catch { return notify('Form fields JSON is invalid', 'error'); }
    }
    let fd;
    try { fd = toFormData(); } catch (error) { return notify(error.message || `Use ${dateFormat} HH:mm for popup dates`, 'error'); }
    setSaving(true);
    try {
      const row = selected ? await updatePopupById(selected.id, fd) : await createPopup(fd);
      await load(); selectPopup(row); notify(selected ? 'Popup updated' : 'Popup created');
    } catch (error) { notify(error.response?.data?.error || 'Failed to save popup', 'error'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!selected) return;
    try {
      await deletePopupById(selected.id); setConfirmDelete(false); setSelected(null); setForm(emptyPopup); setImagePreview(''); await load(); notify('Popup deleted');
    } catch { notify('Failed to delete popup', 'error'); }
  };

  const weekdays = selectedWeekdayNumbers(form.days_of_week);
  if (loading) return <div className="storefront-popup__loading"><span className="crm-button__spinner" aria-label="Loading popups" /></div>;

  return (
    <div className="storefront-popup">
      <header className="storefront-popup__page-header">
        <div><h2>Popups</h2><p>Create and target storefront popups by page, schedule, trigger and frequency.</p></div>
        <CrmButton variant="primary" startIcon={<AddIcon />} onClick={newPopup}>New popup</CrmButton>
      </header>

      <div className="storefront-popup__layout">
        <CrmCard title="Popups" className="storefront-popup__list-card">
          <div className="storefront-popup__list">
            {popups.map((popup) => (
              <button key={popup.id} type="button" className={`storefront-popup__list-item ${selected?.id === popup.id ? 'is-active' : ''}`} onClick={() => selectPopup(popup)}>
                <span>{popup.name || popup.title || `Popup #${popup.id}`}</span><span className={`storefront-popup__status ${popup.is_active ? 'is-on' : ''}`}>{popup.is_active ? 'Active' : 'Off'}</span>
              </button>
            ))}
            {!popups.length ? <p className="storefront-popup__empty">No popups yet.</p> : null}
          </div>
        </CrmCard>

        <CrmCard
          title={selected ? 'Edit popup' : 'New popup'}
          actions={<div className="storefront-popup__card-actions"><CrmButton variant="primary" startIcon={<SaveIcon />} loading={saving} onClick={save}>Save</CrmButton>{selected ? <CrmButton iconOnly variant="tertiary" onClick={() => setConfirmDelete(true)} aria-label="Delete popup"><DeleteIcon /></CrmButton> : null}</div>}
        >
          <div className="storefront-popup__form">
            <div className="storefront-popup__full"><CrmSwitch label="Enable popup" checked={form.is_active} onChange={(event) => patch('is_active', event.target.checked)} /></div>
            <CrmInput label="Internal name" value={form.name} onChange={(event) => patch('name', event.target.value)} />
            <CrmInput label="Popup title" value={form.title} onChange={(event) => patch('title', event.target.value)} />
            <CrmSelect label="Popup type" value={form.popup_type} onChange={(event) => patch('popup_type', event.target.value)}>{optionList(popupTypes)}</CrmSelect>
            <div className="storefront-popup__full"><CrmInput label="Subtitle" value={form.subtitle} onChange={(event) => patch('subtitle', event.target.value)} /></div>

            <div className="storefront-popup__full storefront-popup__editor"><label>Body content</label><WgiymEditor value={form.body_html} onChange={(value) => patch('body_html', value)} /></div>
            <CrmInput label="CTA text" value={form.cta_text} onChange={(event) => patch('cta_text', event.target.value)} />
            <CrmInput label="CTA link" value={form.cta_link} onChange={(event) => patch('cta_link', event.target.value)} />

            <div className="storefront-popup__full storefront-popup__media">
              <label>Popup media</label>
              {imagePreview ? <img src={imagePreview} alt="Popup" /> : null}
              <MediaAssetInput ref={mediaRef} onSelect={(asset) => { patch('image_url', asset.url); setImagePreview(mediaUrl(asset.url)); }} />
              <div className="storefront-popup__inline-actions"><CrmButton variant="secondary" onClick={() => mediaRef.current?.click()}>{imagePreview ? 'Change media' : 'Choose media'}</CrmButton>{imagePreview ? <CrmButton variant="tertiary" onClick={() => { patch('image_url', ''); setImagePreview(''); }}>Remove</CrmButton> : null}</div>
            </div>

            <CrmSelect label="Display target" value={form.target_scope} onChange={(event) => patch('target_scope', event.target.value)}>{optionList(scopes)}</CrmSelect>
            <CrmSelect label="Trigger" value={form.trigger_type} onChange={(event) => patch('trigger_type', event.target.value)}>{optionList(triggers)}</CrmSelect>
            {form.target_scope === 'selected_pages' ? (
              <div className="storefront-popup__full crm-field"><label className="crm-field__label" htmlFor="popup-pages">CMS pages</label><select id="popup-pages" multiple className="crm-control storefront-popup__multi-select" value={splitCsv(form.target_page_slugs)} onChange={(event) => patch('target_page_slugs', Array.from(event.target.selectedOptions).map((option) => option.value).join(','))}>{pages.map((page) => <option key={page.id} value={page.slug}>{page.title || page.slug} · /{page.slug}</option>)}</select><span className="crm-field__message">Use Ctrl/Cmd to select multiple pages.</span></div>
            ) : null}
            <div className="storefront-popup__full"><CrmInput label="Excluded paths" value={form.excluded_paths} onChange={(event) => patch('excluded_paths', event.target.value)} helperText="Comma-separated storefront paths, e.g. /checkout,/success" /></div>

            <div className="storefront-popup__full"><label className="storefront-popup__section-label">Days of week</label><div className="storefront-popup__days">{WEEKDAYS.map((day, index) => { const dayNumber = weekdayNumberForIndex(index); const active = weekdays.has(dayNumber); return <button key={day} type="button" className={`storefront-popup__day ${active ? 'is-active' : ''}`} onClick={() => { const next = new Set(weekdays); if (active) next.delete(dayNumber); else next.add(dayNumber); patch('days_of_week', [...next].sort((a, b) => a - b).join(',')); }}>{day.charAt(0) + day.slice(1).toLowerCase()}</button>; })}</div><span className="crm-field__message">Leave all unselected to allow every day.</span></div>

            <CrmInput label="Cooldown days" type="number" value={form.cooldown_days} onChange={(event) => patch('cooldown_days', Number(event.target.value))} />
            <CrmSelect label="Frequency" value={form.frequency} onChange={(event) => patch('frequency', event.target.value)}>{optionList(frequencies)}</CrmSelect>
            <CrmSwitch label="Lead form enabled" checked={form.form_enabled} onChange={(event) => patch('form_enabled', event.target.checked)} />
            <CrmSwitch label="Slots available / allow show" checked={form.availability_enabled} onChange={(event) => patch('availability_enabled', event.target.checked)} />
            <CrmSwitch label="Max once per session" checked={form.max_once_per_session} onChange={(event) => patch('max_once_per_session', event.target.checked)} />
            {form.form_enabled ? <div className="storefront-popup__full"><CrmTextarea label="Form fields JSON" value={form.form_fields_json} onChange={(event) => patch('form_fields_json', event.target.value)} rows={3} helperText="Default fields are name and WhatsApp number." /></div> : null}
            <CrmInput label="Delay seconds" type="number" value={form.delay_seconds} onChange={(event) => patch('delay_seconds', Number(event.target.value))} />
            <CrmInput label="Scroll percent" type="number" value={form.scroll_percent} onChange={(event) => patch('scroll_percent', Number(event.target.value))} helperText="Used only for scroll trigger." />
            <CrmInput label="Sort order" type="number" value={form.display_order} onChange={(event) => patch('display_order', Number(event.target.value))} />
            <CrmInput label={`Show from (${dateFormat} HH:mm)`} value={form.starts_at} onChange={(event) => patch('starts_at', event.target.value)} placeholder={`${dateFormat} HH:mm`} />
            <CrmInput label={`Show until (${dateFormat} HH:mm)`} value={form.ends_at} onChange={(event) => patch('ends_at', event.target.value)} placeholder={`${dateFormat} HH:mm`} />
          </div>
        </CrmCard>
      </div>

      <ConfirmDialog open={confirmDelete} title="Delete popup?" message={`Delete ${selected?.name || selected?.title || 'this popup'}? This action cannot be undone.`} onCancel={() => setConfirmDelete(false)} onConfirm={remove} />
      <NotificationSnackbar open={notification.open} message={notification.message} severity={notification.severity} onClose={() => setNotification((current) => ({ ...current, open: false }))} />
    </div>
  );
}
